import { GoogleApiWrapper, Map, Marker } from "google-maps-react";
import React, { useEffect, useState, useCallback } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import GooglePlacesAutocomplete, {
  geocodeByLatLng,
  geocodeByPlaceId,
  getLatLng,
} from "react-google-places-autocomplete";
import closeIcon from "../../assets/images/close.svg";
import LocationAlert from "./Alertpopus/LocationAlert";

const GOOGLE_MAP_API_KEY = process.env.REACT_APP_GOOGLE_MAP_KEY;

const GoogleMapModal = ({
  show,
  google,
  setUserAddress,
  closeHandler,
  location,
  setLocation,
  defaultAddress = null,
  userAddress,
  setReadOnlyData,
  countries,
}) => {
  const [data, setData] = useState(defaultAddress);
  const [tempLocation, setTempLocation] = useState(location);
  const [showLocationAlert, setShowLocationAlert] = useState(false);
  const [loading, setLoading] = useState(false);

  const getReadOnlyData = useCallback(
    (addressComponent) => {
      let addressObj = {};
      addressComponent.reverse().forEach((item) => {
        if (item.types.includes("country")) {
          const country_id =
            countries?.length &&
            countries.find((country) => country.SortName === item?.short_name)
              ?._id;
          addressObj = { ...addressObj, country: country_id };
        }
        if (item.types.includes("administrative_area_level_1")) {
          addressObj = { ...addressObj, state: item.long_name };
        }
        if (
          item.types.includes("administrative_area_level_3") ||
          item.types.includes("administrative_area_level_2") ||
          item.types.includes("locality")
        ) {
          addressObj = { ...addressObj, city: item.long_name };
        }
      });
      setReadOnlyData(addressObj);
    },
    [countries, setReadOnlyData]
  );

  const handleDefaultAddress = useCallback(
    async (position) => {
      setLoading(true);
      try {
        const res = await geocodeByLatLng(position);
        if (res[0]) {
          // getReadOnlyData is still fine to call as it usually updates detailed fields which might be buffered or not, 
          // but for consistency we should probably buffer it too if possible. 
          // For now, let's keep it as is if it's intended to be "live".
          getReadOnlyData(res[0].address_components);
          setData({
            label: res[0].formatted_address,
            value: { place_id: "" },
          });
          setTempLocation(position);
        }
      } catch (err) {
        console.error("Error in handleDefaultAddress:", err);
      } finally {
        setLoading(false);
      }
    },
    [getReadOnlyData, setData]
  );

  useEffect(() => {
    if (!data?.label && show) {
      const handleLocation = (position) => {
        const { latitude, longitude } = position.coords;
        const pos = { lat: latitude, lng: longitude };

        handleDefaultAddress(pos);
        setTempLocation(pos);
      };

      const handleLocationError = (error) => {
        console.error("Error getting location:", error);
        setShowLocationAlert(true);
      };

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          handleLocation,
          handleLocationError,
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      } else {
        console.error("Geolocation is not supported by this browser.");
        setShowLocationAlert(true);
      }
    }
  }, [show, handleDefaultAddress]);

  useEffect(() => {
    if (data?.value?.place_id) {
      setLoading(true);
      geocodeByPlaceId(data?.value?.place_id)
        .then((results) => {
          if (results[0]) {
            return getLatLng(results[0]).then((res) => {
              setTempLocation(res);
              return geocodeByLatLng(res);
            });
          }
          throw new Error("No results from geocodeByPlaceId");
        })
        .then((res) => {
          if (res[0]) {
            getReadOnlyData(res[0].address_components);
          }
        })
        .catch((err) => {
          console.error("Error in place ID geocoding:", err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [data, getReadOnlyData]);

  const onMarkerDragEnd = useCallback(
    async (coord) => {
      const { latLng } = coord;
      const lat = latLng.lat();
      const lng = latLng.lng();
      const position = { lat, lng };

      setLoading(true);
      try {
        const res = await geocodeByLatLng(position);
        if (res[0]) {
          getReadOnlyData(res[0].address_components);
          setData({
            label: res[0].formatted_address,
            value: { place_id: "" },
          });
          setTempLocation(position);
        }
      } catch (err) {
        console.error("Error in onMarkerDragEnd:", err);
      } finally {
        setLoading(false);
      }
    },
    [getReadOnlyData, setData]
  );

  const handleSave = () => {
    if (data?.label) {
      setUserAddress(data.label);
      setLocation(tempLocation);
      closeHandler();
    }
  };

  const fetchCurrentLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      setShowLocationAlert(true);
      return;
    }

    try {
      const permission = await navigator.permissions.query({
        name: "geolocation",
      });

      if (permission.state === "denied") {
        // Browser will NOT re-prompt automatically
        setShowLocationAlert(true);
        return;
      }

      // granted OR prompt → browser will ask if needed
      setLoading(true);

    navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const pos = { lat: latitude, lng: longitude };

          try {
            const res = await geocodeByLatLng(pos);
            if (res[0]) {
              getReadOnlyData(res[0].address_components);
              setData({
                label: res[0].formatted_address,
                value: { place_id: "" },
              });
              setTempLocation(pos);
            }
          } catch (err) {
            console.error("Reverse geocode failed:", err);
          } finally {
            setLoading(false);
          }
        },
        (error) => {
          console.error("Geolocation error:", error);
          setLoading(false);
          setShowLocationAlert(true);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    } catch (err) {
      console.error("Permission API error:", err);
      setShowLocationAlert(true);
    }
  }, [getReadOnlyData, setData]);


  return (
    <>
      <Modal
        show={show}
        size="lg"
        onHide={closeHandler}
        contentClassName="signupMap"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header>
          <h6>Select Location</h6>
          <img
            src={closeIcon}
            alt="close"
            className="close-icon"
            onClick={closeHandler}
          />
        </Modal.Header>
        <Modal.Body
          style={{ width: "auto", height: "55vh", position: "relative", padding: 0, color: "#000", overflow: 'hidden' }}
        >
          {loading && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(255, 255, 255, 0.7)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 10,
              }}
            >
              <Spinner animation="border" variant="primary" />
            </div>
          )}

          <div className="location-search-wrapper" style={{ position: 'absolute', top: '15px', left: '15px', right: '15px', zIndex: 5 }}>
            <div style={{ position: 'relative' }}>
              <GooglePlacesAutocomplete
                apiKey={GOOGLE_MAP_API_KEY}
                selectProps={{
                  value: data,
                  onChange: (val) => {
                    if (!val) {
                      setData(null);
                      setUserAddress("");
                    } else {
                      setData(val);
                    }
                  },
                  isClearable: true,
                  className: "location-search",
                  placeholder: "Search for your address...",
                  styles: {
                    control: (provided) => ({
                      ...provided,
                      borderRadius: '10px',
                      padding: '5px 5px 5px 35px',
                      border: 'none',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      backgroundColor: '#fff',
                    }),
                    input: (provided) => ({
                      ...provided,
                      color: '#333',
                    }),
                    singleValue: (provided) => ({
                      ...provided,
                      color: '#333',
                    }),
                    placeholder: (provided) => ({
                      ...provided,
                      color: '#999',
                    }),
                  }
                }}
              />
              <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', zIndex: 1, pointerEvents: 'none' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4A8BBD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
              </div>
            </div>
          </div>

          <Map
            google={google}
            zoom={14}
            style={{
              width: "100%",
              height: "100%",
            }}
            initialCenter={tempLocation}
            center={tempLocation}
            fullscreenControl={false}
            mapTypeControl={false}
            streetViewControl={false}
            key={JSON.stringify(tempLocation)}
          >
            <Marker
              name={"Current Location"}
              position={tempLocation}
              draggable={true}
              onDragend={(t, map, coord) => onMarkerDragEnd(coord)}
              key={JSON.stringify(tempLocation)}
            />
          </Map>

          <button
            type="button"
            className="btn btn-light shadow-sm"
            onClick={fetchCurrentLocation}
            style={{
              position: 'absolute',
              bottom: '20px',
              right: '20px',
              zIndex: 5,
              borderRadius: '50%',
              width: '45px',
              height: '45px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              border: 'none'
            }}
            title="Use Current Location"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4A8BBD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <circle cx="12" cy="12" r="3"></circle>
              <line x1="12" y1="1" x2="12" y2="4"></line>
              <line x1="12" y1="20" x2="12" y2="23"></line>
              <line x1="1" y1="12" x2="4" y2="12"></line>
              <line x1="20" y1="12" x2="23" y2="12"></line>
            </svg>
          </button>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeHandler} className="me-2" style={{ marginTop: 0 }}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading || !data} style={{ marginTop: 0 }}>
            {loading ? "Processing..." : "Select Address"}
          </Button>
        </Modal.Footer>
      </Modal>
      <LocationAlert show={showLocationAlert} setShow={setShowLocationAlert} />
    </>
  );
};

export default GoogleApiWrapper({
  apiKey: GOOGLE_MAP_API_KEY,
})(GoogleMapModal);
